// Maps an approved learner_activities.activity_definition into the
// interaction steps the learner app knows how to show. Nothing here writes
// curriculum; the definition is read as-is and never redesigned (handoff §5).
//
// The live published definitions (verified through Directus, 24 Sep 2026)
// keeps the right answer in `expected` for both Choice and Sequence. Older
// spellings are still accepted. A Choice or Sequence without a usable right
// answer is refused rather than shown, because it would let any answer pass.
//
// Matching, tracing, counting and sorting follow the canonical Foundation
// Phase primitives contract exactly: they have no aliases, so a mistake in new
// authoring is refused before the learner starts rather than guessed at.

import {
  REPRESENTATIONS,
  isNegative,
  isNumericValue,
  exactNumbersSupported,
  isTolerance,
  isZero,
  numericEqual,
} from "./maths/numeric";
import {
  BOUNDED_OPERATORS,
  ExpressionError,
  OPERATORS,
  expressionMatches,
  isExpressionValue,
  parseExpressionValue,
  symbolsIn,
} from "./maths/expression";
import {
  FIGURE_KINDS,
  MODES,
  TRANSFORMS,
  geometryCorrect,
  inRange,
  reachable,
} from "./maths/coordinates";

export class ContractError extends Error {}

// The activity is fine but this browser cannot do what it needs (exact
// numbers). Still refused before interaction, but explained as the device.
export class UnsupportedDeviceError extends ContractError {}

export const CHOICE = "choice";
export const SEQUENCE = "sequence";
export const DRAWING = "drawing";
export const MATCHING = "matching";
export const TRACING = "tracing";
export const COUNTING = "counting";
export const SORTING = "sorting";
export const NUMERIC_ENTRY = "numeric_entry";
export const EXPRESSION_ENTRY = "expression_entry";
export const COORDINATE_PLANE = "coordinate_plane";
export const TABLE_COMPLETION = "table_completion";

export const COUNTING_MODES = ["tap_count", "make_set", "estimate_then_count"];

const KIND_ALIASES = {
  choice: CHOICE,
  recognise: CHOICE,
  recognize: CHOICE,
  sequence: SEQUENCE,
  order: SEQUENCE,
  drawing: DRAWING,
  draw: DRAWING,
  matching: MATCHING,
  tracing: TRACING,
  counting: COUNTING,
  sorting: SORTING,
};

const first = (...values) =>
  values.find((v) => v !== undefined && v !== null && v !== "");

const textOf = (step) =>
  first(step.prompt, step.instruction, step.question, step.title, step.text) ||
  null;

const optionOf = (raw, media) => {
  if (typeof raw === "string") return { value: raw, label: raw };
  const value = first(raw.value, raw.id, raw.key);
  if (value === undefined) {
    throw new ContractError("An option has no value.");
  }
  return {
    value: String(value),
    label: first(raw.label, raw.text, raw.title, String(value)),
    asset: media.image(raw.asset),
    audio: media.audio(raw.audio_asset),
    emoji: first(raw.emoji, raw.icon) || null,
  };
};

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const hasText = (value) => typeof value === "string" && value.trim() !== "";

// An item of a new primitive: an object with its own string value. Labels
// and media are optional.
const itemOf = (raw, id, media) => {
  if (!isObject(raw) || !hasText(raw.value)) {
    throw new ContractError(`${id} has an item without a value.`);
  }
  return {
    value: raw.value,
    label: hasText(raw.label) ? raw.label : null,
    asset: media.image(raw.asset),
    audio: media.audio(raw.audio_asset),
  };
};

// `represented`: every item must have a usable label, picture or sound
// (matching and sorting, contract §7). An image is never required: `-5`, `2x`
// or `Rational number` alone is a complete tile.
const itemsOf = (list, id, media, what, { represented = false } = {}) => {
  if (!Array.isArray(list) || list.length === 0) {
    throw new ContractError(`${id} has no ${what}.`);
  }
  const items = list.map((raw) => itemOf(raw, id, media));
  if (represented && items.some((item) => !item.label && !item.asset && !item.audio)) {
    throw new ContractError(`${id} has ${what} with nothing to show or play.`);
  }
  if (new Set(items.map((item) => item.value)).size !== items.length) {
    throw new ContractError(`${id} has two ${what} with the same value.`);
  }
  return items;
};

// Every item used exactly once, and nothing that is not an item.
const coversExactly = (used, values) =>
  used.length === values.length &&
  new Set(used).size === used.length &&
  used.every((value) => values.includes(value));

const mapMatching = (raw, base, media) => {
  const left = itemsOf(raw.left_items, base.id, media, "left items", { represented: true });
  const right = itemsOf(raw.right_items, base.id, media, "right items", { represented: true });
  const pairs = raw.expected?.pairs;
  if (
    left.length !== right.length ||
    !Array.isArray(pairs) ||
    !pairs.every((p) => isObject(p) && hasText(p.left) && hasText(p.right)) ||
    !coversExactly(pairs.map((p) => p.left), left.map((i) => i.value)) ||
    !coversExactly(pairs.map((p) => p.right), right.map((i) => i.value))
  ) {
    throw new ContractError(`${base.id} has no complete set of pairs.`);
  }
  return {
    ...base,
    left,
    right,
    expectedPairs: pairs.map(({ left: l, right: r }) => ({ left: l, right: r })),
  };
};

// The template is what is traced, so unlike optional media it cannot fall
// back: a template that is missing, not approved, not active or otherwise not
// resolved refuses the activity before the learner starts, rather than
// leaving an empty tracing space.
const mapTracing = (raw, base, media) => {
  const template = hasText(raw.template_asset) ? media.image(raw.template_asset) : null;
  if (!template) {
    throw new ContractError(`${base.id} has no template to trace.`);
  }
  return { ...base, template };
};

const isCount = (value) => Number.isInteger(value) && value >= 0;

const mapCounting = (raw, base, media) => {
  if (!COUNTING_MODES.includes(raw.mode)) {
    throw new ContractError(`${base.id} has no counting mode.`);
  }
  const items = itemsOf(raw.items, base.id, media, "items");
  const count = raw.expected?.count;
  if (!isCount(count) || count > items.length) {
    throw new ContractError(`${base.id} has no count that its items can make.`);
  }
  let numberOptions = null;
  if (raw.number_options !== undefined) {
    const options = raw.number_options;
    if (
      !Array.isArray(options) ||
      options.length < 2 ||
      !options.every(isCount) ||
      new Set(options).size !== options.length
    ) {
      throw new ContractError(`${base.id} has unusable number choices.`);
    }
    numberOptions = [...options];
  }
  return { ...base, mode: raw.mode, items, expectedCount: count, numberOptions };
};

const mapSorting = (raw, base, media) => {
  const groups = itemsOf(raw.groups, base.id, media, "groups", { represented: true });
  if (groups.length < 2) throw new ContractError(`${base.id} needs two groups.`);
  const items = itemsOf(raw.items, base.id, media, "items", { represented: true });
  const assignments = raw.expected?.assignments;
  const groupValues = groups.map((g) => g.value);
  if (
    !Array.isArray(assignments) ||
    !assignments.every(
      (a) => isObject(a) && hasText(a.item) && groupValues.includes(a.group)
    ) ||
    !coversExactly(assignments.map((a) => a.item), items.map((i) => i.value))
  ) {
    throw new ContractError(`${base.id} has no group for every item.`);
  }
  return {
    ...base,
    groups,
    items,
    expectedAssignments: assignments.map(({ item, group }) => ({ item, group })),
  };
};

// --- Senior Phase core mathematics (Senior Phase primitives contract) -------
//
// Each mapper validates every required field, reference and expected answer
// before the learner starts; nothing is aliased or guessed. The returned step
// carries what its screen and grading need, never pixels or display strings.

const fail = (id, why) => {
  throw new ContractError(`${id} ${why}`);
};

const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);
const isBool = (value) => typeof value === "boolean";
const optionalBool = (value, fallback, id, field) => {
  if (value === undefined) return fallback;
  if (!isBool(value)) fail(id, `has a ${field} that is not true or false.`);
  return value;
};
const uniqueIds = (list, id, what) => {
  const ids = list.map((entry) => entry?.id);
  if (!ids.every(hasText) || new Set(ids).size !== ids.length) {
    fail(id, `has ${what} without unique ids.`);
  }
};

const UNIT_POSITIONS = ["prefix", "suffix"];
const unitOf = (raw, id, selection) => {
  if (
    !isObject(raw) ||
    !hasText(raw.value) ||
    !hasText(raw.label) ||
    !UNIT_POSITIONS.includes(raw.position) ||
    raw.selection !== selection
  ) {
    fail(id, `has a unit that cannot be used.`);
  }
  return { value: raw.value, label: raw.label, position: raw.position };
};

// Exact numeric comparison needs BigInt; an older browser without it shows
// the calm "can't be shown" state rather than grading inexactly.
const requireExactNumbers = (id) => {
  if (!exactNumbersSupported()) {
    throw new UnsupportedDeviceError(`${id} needs exact numbers this browser cannot do.`);
  }
};

const mapNumericEntry = (raw, base, media) => {
  const { id } = base;
  requireExactNumbers(id);
  const answer = raw.answer;
  if (!isObject(answer)) fail(id, "has no answer settings.");
  const representations = answer.representations;
  if (
    !Array.isArray(representations) ||
    representations.length === 0 ||
    !representations.every((r) => REPRESENTATIONS.includes(r)) ||
    new Set(representations).size !== representations.length
  ) {
    fail(id, "has no usable answer representations.");
  }
  const allowNegative = optionalBool(answer.allow_negative, true, id, "allow_negative");

  if (answer.unit !== undefined && answer.unit_options !== undefined) {
    fail(id, "has both a fixed unit and unit choices.");
  }
  const unit = answer.unit === undefined ? null : unitOf(answer.unit, id, "fixed");
  let unitOptions = null;
  if (answer.unit_options !== undefined) {
    if (!Array.isArray(answer.unit_options) || answer.unit_options.length < 2) {
      fail(id, "has fewer than two unit choices.");
    }
    unitOptions = answer.unit_options.map((option) => unitOf(option, id, "learner"));
    if (new Set(unitOptions.map((u) => u.value)).size !== unitOptions.length) {
      fail(id, "has two unit choices with the same value.");
    }
  }

  let working = null;
  if (answer.working_space !== undefined) {
    const space = answer.working_space;
    if (!isObject(space) || !isBool(space.enabled)) fail(id, "has unusable working space settings.");
    if (space.enabled) {
      working = {
        // An optional page to work on; missing media leaves a plain space.
        template: hasText(space.background_asset) ? media.image(space.background_asset) : null,
      };
    }
  }

  const expected = raw.expected;
  if (!isObject(expected) || !Array.isArray(expected.values) || expected.values.length === 0) {
    fail(id, "has no expected value.");
  }
  for (const value of expected.values) {
    if (!isNumericValue(value) || !representations.includes(value.kind)) {
      fail(id, "has an expected value the learner cannot enter.");
    }
    if (!allowNegative && isNegative(value)) fail(id, "expects a negative value but forbids negatives.");
  }
  let tolerance = null;
  if (expected.tolerance !== undefined) {
    if (!isTolerance(expected.tolerance)) fail(id, "has an unusable tolerance.");
    if (
      expected.tolerance.type === "relative" &&
      expected.values.some(isZero)
    ) {
      fail(id, "uses a relative tolerance around zero.");
    }
    tolerance = expected.tolerance;
  }

  let expectedUnit = null;
  if (unit) {
    // A fixed unit is the answer's unit whether or not it is repeated.
    if (expected.unit !== undefined && expected.unit !== unit.value) {
      fail(id, "expects a unit other than its fixed unit.");
    }
    expectedUnit = unit.value;
  } else if (unitOptions) {
    if (!unitOptions.some((u) => u.value === expected.unit)) {
      fail(id, "expects a unit that is not one of its choices.");
    }
    expectedUnit = expected.unit;
  } else if (expected.unit !== undefined) {
    fail(id, "expects a unit but offers none.");
  }

  return {
    ...base,
    representations,
    allowNegative,
    unit,
    unitOptions,
    working,
    expectedValues: expected.values,
    expectedUnit,
    tolerance,
  };
};

// Objective interaction correctness only (contract §3.3): a well-formed value
// in an allowed representation, the authored unit, an accepted value.
export const isNumericRight = (step, response) => {
  const value = response?.value;
  if (!isNumericValue(value) || !step.representations.includes(value.kind)) return false;
  if (!step.allowNegative && isNegative(value)) return false;
  if ((step.expectedUnit || null) !== (response.unit || null)) return false;
  return step.expectedValues.some((expected) => numericEqual(value, expected, step.tolerance));
};

const SYMBOL = /^[A-Za-z]+$/;

const mapExpressionEntry = (raw, base) => {
  const { id } = base;
  const answer = raw.answer;
  if (!isObject(answer)) fail(id, "has no answer settings.");
  const { symbols, operators } = answer;
  if (
    !Array.isArray(symbols) ||
    !symbols.every((s) => typeof s === "string" && SYMBOL.test(s) && s !== "sqrt") ||
    new Set(symbols).size !== symbols.length
  ) {
    fail(id, "has unusable symbols.");
  }
  if (
    !Array.isArray(operators) ||
    !operators.every((op) => OPERATORS.includes(op)) ||
    new Set(operators).size !== operators.length
  ) {
    fail(id, "has unusable operators.");
  }
  const allowEquation = optionalBool(answer.allow_equation, false, id, "allow_equation");
  // The one contract every expression in this step is held to: the learner's
  // entry, each accepted form, and both sides of grading.
  const contract = { symbols, operators, allowEquation };
  const accepted = raw.expected?.accepted;
  if (!Array.isArray(accepted) || accepted.length === 0) fail(id, "has no accepted expression.");
  accepted.forEach((form) => requireExpression(form, contract, id, "an accepted expression"));
  return { ...base, symbols, operators, allowEquation, contract, accepted };
};

// An authored expression is trusted no more than a learner's: it must be a
// canonical v1 value that parses under the step's own contract, or the whole
// definition is refused before the learner starts.
const EXPRESSION_PROBLEMS = {
  format: "is not a thuto-expression-v1 value",
  operator: "uses an operation the step does not declare",
  symbol: "uses a symbol the step does not declare",
  equation: "is an equation where equations are not allowed",
};
function requireExpression(value, contract, id, what) {
  try {
    parseExpressionValue(value, contract);
  } catch (error) {
    if (!(error instanceof ExpressionError)) throw error;
    fail(id, `has ${what} that ${EXPRESSION_PROBLEMS[error.reason] || "cannot be read"}.`);
  }
}

export const isExpressionRight = (step, response) =>
  expressionMatches(response?.expression, step.accepted, step.contract);

// Grid lines beyond this are unreadable on a tablet and almost certainly an
// authoring mistake (a step of 0.01 across -10…10).
const MAX_GRID_LINES = 200;

const axisOf = (raw, id, name) => {
  if (
    !isObject(raw) ||
    !isFiniteNumber(raw.min) ||
    !isFiniteNumber(raw.max) ||
    !(raw.min < raw.max) ||
    !isFiniteNumber(raw.step) ||
    !(raw.step > 0) ||
    (raw.max - raw.min) / raw.step > MAX_GRID_LINES
  ) {
    fail(id, `has an unusable ${name} axis.`);
  }
  if (raw.label !== undefined && typeof raw.label !== "string") fail(id, `has an unusable ${name} axis label.`);
  return { min: raw.min, max: raw.max, step: raw.step, label: hasText(raw.label) ? raw.label : null };
};

const pointOf = (raw, id, plane, what) => {
  if (!isObject(raw) || !hasText(raw.id) || !isFiniteNumber(raw.x) || !isFiniteNumber(raw.y)) {
    fail(id, `has ${what} without an id and coordinates.`);
  }
  if (!inRange(plane, raw)) fail(id, `has ${what} outside the plane.`);
  return { id: raw.id, x: raw.x, y: raw.y, label: hasText(raw.label) ? raw.label : null };
};

const figureOf = (raw, id, plane, what) => {
  if (!isObject(raw) || !hasText(raw.id) || !FIGURE_KINDS.includes(raw.kind)) {
    fail(id, `has ${what} that is not a polyline or polygon.`);
  }
  const vertices = Array.isArray(raw.vertices) ? raw.vertices : [];
  if (vertices.length < (raw.kind === "polygon" ? 3 : 2)) fail(id, `has ${what} with too few vertices.`);
  const mapped = vertices.map((v) => pointOf(v, id, plane, `${what} vertices`));
  uniqueIds(mapped, id, `${what} vertices`);
  return { id: raw.id, kind: raw.kind, label: hasText(raw.label) ? raw.label : null, vertices: mapped };
};

const geometryOf = (raw, id, plane, what) => {
  if (raw !== undefined && !isObject(raw)) fail(id, `has unusable ${what} geometry.`);
  const points = raw?.points === undefined ? [] : raw.points;
  const figures = raw?.figures === undefined ? [] : raw.figures;
  if (!Array.isArray(points) || !Array.isArray(figures)) fail(id, `has unusable ${what} geometry.`);
  const mapped = {
    points: points.map((p) => pointOf(p, id, plane, `${what} points`)),
    figures: figures.map((f) => figureOf(f, id, plane, `${what} figures`)),
  };
  uniqueIds(mapped.points, id, `${what} points`);
  uniqueIds(mapped.figures, id, `${what} figures`);
  return mapped;
};

const operationOf = (raw, mode, id, initial) => {
  const operation = raw.operation;
  if (!isObject(operation)) fail(id, "has no operation.");
  if (operation.type !== mode) fail(id, `has an operation that is not ${mode}.`);
  const known = new Set([...initial.points, ...initial.figures].map((g) => g.id));
  const targets = operation.target_ids;
  if (
    !Array.isArray(targets) ||
    targets.length === 0 ||
    !targets.every((t) => known.has(t)) ||
    new Set(targets).size !== targets.length
  ) {
    fail(id, "has targets that are not on the plane.");
  }
  const result = { type: mode, targetIds: targets };
  if (mode === "translate") {
    const v = operation.vector;
    if (!isObject(v) || !isFiniteNumber(v.dx) || !isFiniteNumber(v.dy)) fail(id, "has no translation vector.");
    result.vector = { dx: v.dx, dy: v.dy };
  } else if (mode === "reflect") {
    const axis = operation.axis;
    const valued = axis?.kind === "vertical" || axis?.kind === "horizontal";
    if (
      !isObject(axis) ||
      !["x", "y", "vertical", "horizontal"].includes(axis.kind) ||
      (valued ? !isFiniteNumber(axis.value) : axis.value !== undefined)
    ) {
      fail(id, "has no usable mirror line.");
    }
    result.axis = valued ? { kind: axis.kind, value: axis.value } : { kind: axis.kind };
  } else if (mode === "rotate") {
    const { centre, angle, direction } = operation;
    if (
      !isObject(centre) ||
      !isFiniteNumber(centre.x) ||
      !isFiniteNumber(centre.y) ||
      !isFiniteNumber(angle) ||
      !["clockwise", "counterclockwise"].includes(direction)
    ) {
      fail(id, "has no usable rotation.");
    }
    result.centre = { x: centre.x, y: centre.y };
    result.angle = angle;
    result.direction = direction;
  }
  return result;
};

const mapCoordinatePlane = (raw, base) => {
  const { id } = base;
  const mode = raw.mode;
  if (!MODES.includes(mode)) fail(id, "has a coordinate mode that is not supported.");
  const p = raw.plane;
  if (!isObject(p)) fail(id, "has no plane.");
  const plane = {
    x: axisOf(p.x, id, "x"),
    y: axisOf(p.y, id, "y"),
    showAxes: optionalBool(p.show_axes, true, id, "show_axes"),
    showGrid: optionalBool(p.show_grid, true, id, "show_grid"),
    showTickLabels: optionalBool(p.show_tick_labels, true, id, "show_tick_labels"),
    snap: p.snap,
  };
  if (!isFiniteNumber(plane.snap) || !(plane.snap > 0)) fail(id, "has no usable snap.");

  const initial = geometryOf(raw.initial, id, plane, "starting");
  const expectedRaw = raw.expected;
  if (!isObject(expectedRaw)) fail(id, "has no expected geometry.");
  const expected = geometryOf(expectedRaw, id, plane, "expected");
  let tolerance = 0;
  if (expectedRaw.tolerance !== undefined) {
    if (!isFiniteNumber(expectedRaw.tolerance) || expectedRaw.tolerance < 0) {
      fail(id, "has an unusable tolerance.");
    }
    tolerance = expectedRaw.tolerance;
  }
  expected.tolerance = tolerance;
  const targetPoints = [...expected.points, ...expected.figures.flatMap((f) => f.vertices)];
  if (targetPoints.length === 0) fail(id, "expects no geometry.");
  if (!targetPoints.every((point) => reachable(plane, point, tolerance))) {
    fail(id, "expects a coordinate the learner cannot place.");
  }

  let operation = null;
  if (mode === "plot_point" && (expected.points.length !== 1 || expected.figures.length)) {
    fail(id, "must expect exactly one point.");
  }
  if (mode === "plot_points" && expected.figures.length) fail(id, "must expect points only.");
  if (mode === "read_point") {
    if (!initial.points.length) fail(id, "has no point to read.");
    operation = operationOf(raw, mode, id, initial);
    const readable = new Set(initial.points.map((point) => point.id));
    const wanted = expected.points.map((point) => point.id);
    if (
      expected.figures.length ||
      !operation.targetIds.every((t) => readable.has(t)) ||
      wanted.length !== operation.targetIds.length ||
      !wanted.every((w) => operation.targetIds.includes(w))
    ) {
      fail(id, "must expect exactly its target points.");
    }
  }
  if (TRANSFORMS.includes(mode)) operation = operationOf(raw, mode, id, initial);

  return { ...base, mode, plane, initial, operation, expected };
};

export const isGeometryRight = (step, response) => geometryCorrect(step.expected, response);

const ENTRY_TYPES = ["numeric", "expression"];

// A table cell declares no symbols or operators of its own (contract §6.3).
// Its expressions use the same thuto-expression-v1 grammar under this default
// contract: the symbols the authored expressions name, every bounded
// operator except "=", and no equations.
const tableContract = (symbols) => ({
  symbols,
  operators: BOUNDED_OPERATORS,
  allowEquation: false,
});

const mapTableCompletion = (raw, base) => {
  const { id } = base;
  if (!["table", "flow_diagram"].includes(raw.mode)) fail(id, "has a table mode that is not supported.");
  const { columns, rows } = raw;
  if (!Array.isArray(columns) || columns.length === 0) fail(id, "has no columns.");
  if (!Array.isArray(rows) || rows.length === 0) fail(id, "has no rows.");
  uniqueIds(columns, id, "columns");
  uniqueIds(rows, id, "rows");
  const columnIds = new Set(columns.map((c) => c.id));

  const expectedValues = raw.expected?.values;
  if (!isObject(expectedValues)) fail(id, "has no expected values.");
  const cellIds = new Set();
  const editable = [];

  const mappedRows = rows.map((row) => {
    if (!Array.isArray(row.cells) || row.cells.length === 0) fail(id, "has a row without cells.");
    const usedColumns = new Set();
    const cells = row.cells.map((cell) => {
      if (!isObject(cell) || !hasText(cell.id) || cellIds.has(cell.id)) fail(id, "has cells without unique ids.");
      cellIds.add(cell.id);
      if (!columnIds.has(cell.column) || usedColumns.has(cell.column)) {
        fail(id, "has a cell in a column that does not exist or is used twice.");
      }
      usedColumns.add(cell.column);
      if (cell.editable === true) {
        if (!ENTRY_TYPES.includes(cell.entry_type) || cell.value !== undefined) {
          fail(id, "has an editable cell that cannot be entered.");
        }
        editable.push(cell);
        return { id: cell.id, column: cell.column, editable: true, entryType: cell.entry_type };
      }
      if (cell.editable !== undefined && cell.editable !== false) fail(id, "has a cell that is neither fixed nor editable.");
      const fixed = cell.value;
      if (isNumericValue(fixed)) return { id: cell.id, column: cell.column, editable: false, value: fixed };
      if (isExpressionValue(fixed)) {
        requireExpression(fixed, tableContract(symbolsIn(fixed.source)), id, "a fixed cell expression");
        return { id: cell.id, column: cell.column, editable: false, value: fixed };
      }
      return fail(id, "has a fixed cell without a value.");
    });
    return { id: row.id, label: hasText(row.label) ? row.label : null, cells };
  });

  if (editable.length === 0) fail(id, "has nothing to complete.");
  if (editable.some((cell) => cell.entry_type === "numeric")) requireExactNumbers(id);
  const expectedKeys = Object.keys(expectedValues);
  if (
    expectedKeys.length !== editable.length ||
    !editable.every((cell) => expectedKeys.includes(cell.id))
  ) {
    fail(id, "must expect exactly one value for every editable cell.");
  }

  // The keypad of an expression cell offers the symbols its accepted forms
  // use: the definition declares no others, and a rule's own variable (the x
  // in 2*x+1) would only invite answers that cannot be accepted.
  const expectations = {};
  for (const cell of editable) {
    const expectation = expectedValues[cell.id];
    if (cell.entry_type === "numeric") {
      const values = expectation?.values;
      if (!Array.isArray(values) || values.length === 0 || !values.every(isNumericValue)) {
        fail(id, "has a numeric cell without expected values.");
      }
      if (expectation.tolerance !== undefined && !isTolerance(expectation.tolerance)) {
        fail(id, "has a cell with an unusable tolerance.");
      }
      expectations[cell.id] = { values, tolerance: expectation.tolerance || null };
    } else {
      const accepted = expectation?.accepted;
      if (!Array.isArray(accepted) || accepted.length === 0 || !accepted.every(isExpressionValue)) {
        fail(id, "has an expression cell without accepted expressions.");
      }
      const contract = tableContract(symbolsIn(accepted.map((a) => a.source).join(" ")));
      accepted.forEach((form) => requireExpression(form, contract, id, "an accepted cell expression"));
      expectations[cell.id] = { accepted, symbols: contract.symbols, contract };
    }
  }

  const rule = raw.rule === undefined ? null : raw.rule;
  if (rule !== null && (!isObject(rule) || (rule.display !== undefined && !hasText(rule.display)))) {
    fail(id, "has an unusable rule.");
  }
  if (rule?.expression !== undefined) {
    const symbols = isExpressionValue(rule.expression) ? symbolsIn(rule.expression.source) : [];
    requireExpression(rule.expression, tableContract(symbols), id, "a rule expression");
  }

  return {
    ...base,
    mode: raw.mode,
    columns: columns.map((c) => ({ id: c.id, label: hasText(c.label) ? c.label : null })),
    rows: mappedRows,
    rule: rule?.display ? { display: rule.display } : null,
    expectations,
  };
};

// The ids of editable cells whose entry is missing or not accepted.
export const tableCellsWrong = (step, response) =>
  Object.entries(step.expectations)
    .filter(([cellId, expectation]) => {
      const value = response?.values?.[cellId];
      if (expectation.values) {
        return !expectation.values.some((e) => numericEqual(value, e, expectation.tolerance));
      }
      return !expressionMatches(value, expectation.accepted, expectation.contract);
    })
    .map(([cellId]) => cellId);

const STRICT = {
  [MATCHING]: mapMatching,
  [TRACING]: mapTracing,
  [COUNTING]: mapCounting,
  [SORTING]: mapSorting,
  [NUMERIC_ENTRY]: mapNumericEntry,
  [EXPRESSION_ENTRY]: mapExpressionEntry,
  [COORDINATE_PLANE]: mapCoordinatePlane,
  [TABLE_COMPLETION]: mapTableCompletion,
};

const mapStep = (raw, index, media) => {
  if (!isObject(raw)) throw new ContractError(`Step ${index + 1} cannot be shown.`);
  if (STRICT[raw.type]) {
    if (!hasText(raw.id) || !hasText(raw.prompt)) {
      throw new ContractError(`Step ${index + 1} has no id or prompt.`);
    }
    return STRICT[raw.type](
      raw,
      {
        id: raw.id,
        kind: raw.type,
        prompt: raw.prompt,
        promptAudio: media.audio(raw.prompt_audio_asset),
      },
      media
    );
  }

  const id = first(raw.id, raw.key, raw.step_id);
  const kind = KIND_ALIASES[String(first(raw.type, raw.kind, "")).toLowerCase()];
  if (!id || !kind || STRICT[kind]) {
    throw new ContractError(`Step ${index + 1} cannot be shown.`);
  }
  const base = {
    id: String(id),
    kind,
    prompt: textOf(raw),
    promptAudio: media.audio(raw.prompt_audio_asset),
  };

  if (kind === CHOICE) {
    const options = (first(raw.options, raw.choices) || []).map((option) =>
      optionOf(option, media)
    );
    if (options.length < 2) throw new ContractError(`${id} has no choices.`);
    const correct = first(raw.expected, raw.correct, raw.answer, raw.correct_value);
    if (correct == null || !options.some((o) => o.value === String(correct))) {
      throw new ContractError(`${id} has no right answer among its choices.`);
    }
    return {
      ...base,
      stimulus: media.image(raw.stimulus_asset),
      options,
      correct: String(correct),
    };
  }

  if (kind === SEQUENCE) {
    const items = (first(raw.items, raw.options, raw.cards) || []).map((item) =>
      optionOf(item, media)
    );
    if (items.length < 2) throw new ContractError(`${id} has nothing to order.`);
    const order = first(raw.expected, raw.correct_order, raw.correct, raw.answer);
    const values = items.map((item) => item.value).sort();
    const expected = Array.isArray(order) ? order.map(String) : null;
    if (!expected || expected.length !== values.length ||
        [...expected].sort().some((v, i) => v !== values[i])) {
      throw new ContractError(`${id} has no right order for its cards.`);
    }
    return { ...base, items, correctOrder: expected };
  }

  // Drawing: an optional non-answer background under the learner's lines.
  return { ...base, template: media.image(raw.background_asset) };
};

export const toActivity = (record) => {
  let definition = record?.activity_definition;
  if (typeof definition === "string") {
    try {
      definition = JSON.parse(definition);
    } catch {
      throw new ContractError("The activity definition is not readable.");
    }
  }
  // The canonical envelope (primitives contract §2.1): `version: 1` and a
  // non-empty `steps` array. Another version, or no version, may mean
  // something this app does not know, so it is refused rather than guessed at.
  if (!isObject(definition) || definition.version !== 1) {
    throw new ContractError("The activity is not in a version this app can show.");
  }
  const rawSteps = definition.steps;
  if (!Array.isArray(rawSteps) || rawSteps.length === 0) {
    throw new ContractError("The activity has no steps.");
  }
  // Keys resolve only through what the gateway approved for this activity;
  // a key is never read as a file id or an address. Whether a key is a
  // picture or a sound comes from the field it is written in.
  const resolved = record?.resolved_assets || {};
  const resolveAsset = (key) => {
    const asset = typeof key === "string" ? resolved[key] : null;
    return asset && typeof asset.src === "string"
      ? { src: asset.src, alt: asset.alt || "" }
      : null;
  };
  const media = { image: resolveAsset, audio: resolveAsset };
  const steps = rawSteps.map((step, index) => mapStep(step, index, media));
  if (new Set(steps.map((step) => step.id)).size !== steps.length) {
    throw new ContractError("Two steps share an id.");
  }
  return {
    id: record.id,
    title: record.title || null,
    steps,
  };
};

// Gentle feedback only; this is never a grade (handoff §12).
// The adapter guarantees a right answer, so nothing passes by default.
export const isChoiceRight = (step, value) =>
  step.correct != null && step.correct === value;

export const isOrderRight = (step, order) =>
  Array.isArray(step.correctOrder) &&
  step.correctOrder.length === order.length &&
  step.correctOrder.every((value, i) => value === order[i]);

// Pairs and assignments are relationships: the order they were made in, or
// written in, never matters.
const sameRelations = (expected, actual, a, b) => {
  if (!Array.isArray(actual) || actual.length !== expected.length) return false;
  const want = new Set(expected.map((r) => `${r[a]}\u0000${r[b]}`));
  const got = new Set(actual.map((r) => `${r?.[a]}\u0000${r?.[b]}`));
  return got.size === want.size && [...got].every((key) => want.has(key));
};

export const arePairsRight = (step, pairs) =>
  sameRelations(step.expectedPairs, pairs, "left", "right");

export const areAssignmentsRight = (step, assignments) =>
  sameRelations(step.expectedAssignments, assignments, "item", "group");

export const isCountRight = (step, count) => count === step.expectedCount;
